/**
 * SPA-side auth helpers for insights-admin.
 *
 * The browser never speaks OIDC directly — that flow lives on **insights-api**,
 * which is the relying party for the admin realm. It moved there from this
 * app's own Fastify backend; the property that the browser holds no IdP token
 * is unchanged, only the server keeping it. See
 * `docs/legacy/plans/insights-admin-static.md`.
 *
 * The SPA only:
 *
 *   - Reads `GET /api/admin/auth/me` once on first navigation.
 *   - Sends the user to `GET /api/admin/auth/login?return_to=…` when an
 *     unauthenticated route is requested.
 *   - POSTs `/api/admin/auth/logout`, then follows the IdP end-session URL if
 *     one came back.
 *
 * Every call is **cross-origin with `credentials: "include"`** — the admin
 * session cookie lives on the insights-api origin, scoped to `/api/admin`.
 *
 * Session payload stays trimmed relative to the dashboard's: operators carry no
 * `client_key` / `client_name` because they aren't tenants, and App.vue's chip
 * is a fixed "Admin" label.
 *
 * **The role gate lives at login.** insights-api rejects a sign-in that lacks
 * the operator role with a 403 and a message naming it, *before* creating a
 * session. {@link loadCurrentAdmin} surfaces that as `roleDenied` so the login
 * view can explain it — otherwise the user would authenticate successfully and
 * then get bare 403s on every call with no clue why.
 */

import { ref } from "vue";

import { CSRF_HEADER, INSIGHTS_API_URL } from "./env.ts";

/** Subset of admin-session claims the SPA renders. Mirrors `/auth/me`. */
export interface CurrentAdmin {
  sub: string;
  email: string | null;
  name: string | null;
  preferred_username: string | null;
}

export const currentAdmin = ref<CurrentAdmin | null>(null);

/**
 * True when the last sign-in attempt authenticated but lacked the operator
 * role.
 *
 * Distinct from "signed out": the person proved who they are and is still not
 * allowed in, so the login view should say so rather than offering the sign-in
 * button again — which would loop them through the IdP to the same outcome.
 */
export const roleDenied = ref(false);

let loaded = false;

/** Lazy fetch of `/api/admin/auth/me`; idempotent for the page lifetime. */
export const loadCurrentAdmin = async (): Promise<CurrentAdmin | null> => {
  if (loaded) return currentAdmin.value;
  try {
    // Bare `fetch`, not `apiFetch`: a 401 here is the *expected* signed-out
    // answer the router guard acts on, and routing it through the wrapper would
    // redirect straight to login — turning "am I signed in?" into "sign in
    // now" and breaking the `/login` route itself.
    const r = await fetch(`${INSIGHTS_API_URL}/api/admin/auth/me`, {
      credentials: "include",
    });
    if (r.ok) {
      currentAdmin.value = (await r.json()) as CurrentAdmin;
    } else {
      currentAdmin.value = null;
      // 403 here means the session surface refused the role. Preserved so the
      // login view can explain rather than silently showing a sign-in button.
      roleDenied.value = r.status === 403;
    }
  } catch {
    currentAdmin.value = null;
  }
  loaded = true;
  return currentAdmin.value;
};

/** Hand off to insights-api's `/api/admin/auth/login`. */
export const login = (returnTo: string = window.location.pathname): void => {
  const url = new URL(`${INSIGHTS_API_URL}/api/admin/auth/login`);
  // Kept root-relative and guarded against protocol-relative `//host`, which
  // the browser treats as absolute. insights-api validates it again server-side
  // and resolves it onto the admin frontend origin.
  if (returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")) {
    url.searchParams.set("return_to", returnTo);
  }
  window.location.assign(url.toString());
};

/**
 * Destroy the session on insights-api and follow the IdP end-session URL when
 * one is returned. Always navigates, even on failure, so the operator is never
 * left on a page whose session may already be gone.
 *
 * The CSRF header is sent even though this route sits outside the cookie-auth
 * zone: a custom header forces a CORS preflight, and insights-api only answers
 * preflights from allow-listed origins, so it still prevents a cross-origin
 * form post from logging someone out.
 */
export const logout = async (): Promise<void> => {
  let redirect = "/";
  try {
    const r = await fetch(`${INSIGHTS_API_URL}/api/admin/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { [CSRF_HEADER]: "1" },
    });
    if (r.ok) {
      // Shape is `{ ok, end_session_url }`; null when the IdP advertises no
      // `end_session_endpoint`, in which case the local fallback is correct —
      // local logout has already succeeded.
      const body = (await r.json()) as { end_session_url?: string | null };
      if (typeof body.end_session_url === "string" && body.end_session_url) {
        redirect = body.end_session_url;
      }
    }
  } catch {
    // Navigate regardless — the cookie may already be cleared.
  }
  loaded = false;
  currentAdmin.value = null;
  roleDenied.value = false;
  window.location.assign(redirect);
};
